import useSWR from 'swr';
import { API, IS_STAGING } from '../consts';
import { get, post } from './api';
import _ from 'lodash';
import { IntegrationType } from './types/partners';
import React from 'react';

const getNoPropertiesFoundMsg = (integration) => {
  return `We couldn’t find any new properties. Please ensure all of your ${integration} properties are up-to-date.`;
};

const getErrorFetchingMsg = (integration) => {
  return `Error connecting to ${integration}.`;
};

const getIntegrationErrorMessage = (response, fallback) => {
  if (response?.data?.detail) {
    return response.data.detail;
  }

  if (response?.data?.message) {
    return response.data.message;
  }

  if (response?.data?.error) {
    return response.data.error;
  }

  return fallback;
};

export const ownerRezApi = {
  authenticate: () => {
    window.location.href = `${API.root}auth/login/ownerrez/`;
  },
  getUser: async function () {
    return get('/services/ownerrez/user/');
  },
  getProperties: async function () {
    const response = await get('/services/ownerrez/properties/', null, {
      params: {
        includeDisabled: false,
      },
    });
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.OWNERREZ)),
      );
      error.info = response?.data ?? [];
      throw error;
    }

    // Removed filter for USA property
    const properties = response?.data;

    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.OWNERREZ));
      error.status = 'STAY_IN_IMPORT_PROPERTIES';
      error.info = [];
      throw error;
    }

    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/ownerrez/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), { revalidateOnFocus: false });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const hospitableConnectApi = {
  authenticate: async () => {
    let error = null;
    const connection = await post('/services/hospitableconnect/connect/');
    if (!connection.ok) {
      error = new Error(
        getIntegrationErrorMessage(
          connection,
          getErrorFetchingMsg(IntegrationType.HOSPITABLE_CONNECT),
        ),
      );
      error.info = connection?.data ?? [];
      throw error;
    }
    window.location.href = connection.data.returnUrl;
  },
  getProperties: async function () {
    const response = await get('/services/hospitableconnect/listings/');
    let error = null;
    if (!response.ok) {
      error = new Error(
        getIntegrationErrorMessage(
          response,
          getErrorFetchingMsg(IntegrationType.HOSPITABLE_CONNECT),
        ),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    const properties = response.data;
    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.HOSPITABLE_CONNECT));
      error.info = [];
      throw error;
    }
    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/hospitableconnect/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), { revalidateOnFocus: false });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const hospitableApi = {
  authenticate: async () => {
    window.location.href = `${API.root}auth/login/hospitable/`;
  },
  getProperties: async function () {
    const response = await get('/services/hospitable/properties/');
    let error = null;
    if (!response.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.HOSPITABLE)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    const properties = response.data;
    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.HOSPITABLE));
      error.info = [];
      throw error;
    }
    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/hospitable/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), { revalidateOnFocus: false });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const guestyApi = {
  getGuestyOauth: async function (utmSource = 'guesty') {
    const response = await get('/services/guesty/oauth/get/', null, {
      params: {
        utm_source: utmSource,
      },
    });
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.GUESTY)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    return response;
  },

  useGuestyOauth: function (utmSource = 'guesty') {
    const key = `/overview/partners/landing/${utmSource}`;
    const { data, error } = useSWR(key, () => this.getGuestyOauth(utmSource), {
      revalidateOnFocus: false,
    });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
    };
  },

  createOauth: async function ({ owner, integrationToken, utmSource = 'guesty' }) {
    const url = '/services/guesty/oauth/';
    return await post(url, {
      user: owner,
      integrationToken,
      utm_source: utmSource,
    });
  },

  getProperties: async function (utmSource = 'guesty') {
    const response = await get('/services/guesty/properties/', null, {
      params: {
        utm_source: utmSource,
      },
    });
    let error = null;
    if (!response.ok) {
      error = new Error(
        getErrorFetchingMsg(
          utmSource === IntegrationType.GUESTY_LITE.replace(' ', '_').toLowerCase()
            ? IntegrationType.GUESTY_LITE
            : IntegrationType.GUESTY,
        ),
      );
      error.info = [];
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.GUESTY)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    const properties = response.data;
    if (!properties.length) {
      error = new Error(
        getNoPropertiesFoundMsg(
          utmSource === IntegrationType.GUESTY_LITE.replace(' ', '_').toLowerCase()
            ? IntegrationType.GUESTY_LITE
            : IntegrationType.GUESTY,
        ),
      );
      error.info = [];
      throw error;
    }

    return response;
  },
  usePropertyList: function (utmSource = 'guesty') {
    const key = `/overview/partners/${utmSource}/properties`;
    const { data, error } = useSWR(key, () => this.getProperties(utmSource), {
      revalidateOnFocus: false,
    });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const hostawayApi = {
  getHostawayOauth: async function () {
    const response = await get('/services/hostaway/oauth/get/');
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.HOSTAWAY)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    return response;
  },

  useHostawayOauth: function () {
    const key = '/overview/partners/landing/hostaway';
    const { data, error } = useSWR(key, () => this.getHostawayOauth(), {
      revalidateOnFocus: false,
    });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
    };
  },

  createOauth: async function ({ accountId, apiKey }) {
    const url = '/services/hostaway/oauth/';
    return await post(url, { accountId, apiKey });
  },

  getProperties: async function () {
    const response = await get('/services/hostaway/properties/');
    let error = null;
    if (!response.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.HOSTAWAY)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    const properties = response.data;
    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.HOSTAWAY));
      error.info = [];
      throw error;
    }

    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/hostaway/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), { revalidateOnFocus: false });

    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const lodgifyApi = {
  getLodgifyOauth: async function () {
    const response = await get('/services/lodgify/oauth/get/');
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.LODGIFY)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    return response;
  },

  useLodgifyOauth: function () {
    const key = '/overview/partners/landing/lodgify';
    const { data, error } = useSWR(key, () => this.getLodgifyOauth(), {
      revalidateOnFocus: false,
    });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
    };
  },

  createOauth: async function ({ apiKey }) {
    const url = '/services/lodgify/oauth/';
    return await post(url, { apiKey });
  },

  getProperties: async function () {
    const response = await get('/services/lodgify/properties/');
    let error = null;
    if (!response.ok) {
      error = new Error(getErrorFetchingMsg(IntegrationType.LODGIFY));
      error.info = [];
      throw error;
    }
    const properties = response.data;
    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.LODGIFY));
      error.info = [];
      throw error;
    }

    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/lodgify/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), {
      revalidateOnFocus: false,
    });

    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const turnoApi = {
  authenticate: () => {
    window.location.href = `${API.root}auth/login/turno/`;
  },
  getUser: async function () {
    return get('/services/turno/user/');
  },
  getProperties: async function () {
    const response = await get('/services/turno/properties/', null, {});
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.TURNO)),
      );
      error.info = response?.data ?? [];
      throw error;
    }

    // Removed filter for USA property
    const properties = response?.data;

    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.TURNO));
      error.status = 'STAY_IN_IMPORT_PROPERTIES';
      error.info = [];
      throw error;
    }

    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/turno/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), { revalidateOnFocus: false });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const hostfullyApi = {
  authenticate: () => {
    window.location.href = `${API.root}services/hostfully/oauth/start/`;
  },
  getProperties: async function () {
    const response = await get('/services/hostfully/properties/', null, {});

    if (!response?.ok) {
      const error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.HOSTFULLY)),
      );
      error.info = response?.data ?? [];
      throw error;
    }

    const properties = response?.data;

    if (!properties || !properties.length) {
      return {
        ok: true,
        isCustomError: true,
        status: 'STAY_IN_IMPORT_PROPERTIES',
        info: {
          messageBeforeLink: 'Access confirmed! Click on the ',
          messageAfterLink:
            ' and toggle on the properties you’d like to use. Then come back to this page.',
          actionLabel: 'Host Co Integration Page',
          actionUrl: IS_STAGING
            ? 'https://sandbox.hostfully.com/app/#/integrations/details/thehostco'
            : 'https://platform.hostfully.com/app/#/integrations/details/thehostco',
        },
      };
    }

    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/hostfully/properties';

    const { data, error, mutate, isValidating } = useSWR(key, () => this.getProperties(), {
      revalidateOnFocus: true,
      shouldRetryOnError: false,
      dedupingInterval: 0,
    });

    const isCustomError = data && data.isCustomError;

    return {
      data: isCustomError ? [] : data?.data ?? [],
      isLoading: (!error && !data) || isValidating,
      isError: !!error || isCustomError || (data && !data.ok),
      error: isCustomError ? data : error,

      refetch: mutate,
    };
  },
};

export const hostToolsApi = {
  getHostToolsOauth: async function () {
    const response = await get('/services/hosttools/oauth/get/');
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.HOST_TOOLS)),
      );
      error.info = response?.data ?? [];
      throw error;
    }
    return response;
  },

  useHostToolsOauth: function () {
    const key = '/overview/partners/landing/hosttools';
    const { data, error } = useSWR(key, () => this.getHostToolsOauth(), {
      revalidateOnFocus: false,
    });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
    };
  },

  createOauth: async function ({ owner, integrationToken }) {
    const url = '/services/hosttools/oauth/';
    return await post(url, { user: owner, integrationToken });
  },

  getProperties: async function () {
    const response = await get('/services/hosttools/properties/', null, {});
    let error = null;

    if (!response?.ok) {
      error = new Error(
        getIntegrationErrorMessage(response, getErrorFetchingMsg(IntegrationType.HOST_TOOLS)),
      );
      error.info = response?.data ?? [];
      throw error;
    }

    // Removed filter for USA property
    const properties = response?.data;

    if (!properties.length) {
      error = new Error(getNoPropertiesFoundMsg(IntegrationType.HOST_TOOLS));
      error.status = 'STAY_IN_IMPORT_PROPERTIES';
      error.info = [];
      throw error;
    }

    return response;
  },
  usePropertyList: function () {
    const key = '/overview/partners/hosttools/properties';
    const { data, error } = useSWR(key, () => this.getProperties(), { revalidateOnFocus: false });
    return {
      data: data?.data ?? error,
      isLoading: !error && !data,
      isError: !data?.ok || Boolean(error),
      error: _.get(error, 'message'),
    };
  },
};

export const INTEGRATION_INSTRUCTIONS = (partner) => (
  <>
    <span>
      Note: Only one Host Co account can be linked to one {partner} account. <p></p> <br></br>To
      disconnect your {partner} and Host Co account, re-initiate the integration and use your new
      login for either {partner} or The Host Co. This will update the connection to your new account
      and remove the connection from your original account. For additional help, contact{' '}
      <a href="mailto: support@thehost.co">support@thehost.co</a>
    </span>
  </>
);
